import React, { useState } from 'react';
import { Dialog } from '@mui/material';
import { Close, CalendarMonth, CheckCircle } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/sv';

interface ExtendRentalModalProps {
  open: boolean;
  onClose: () => void;
  currentEndDate: string;
  apartmentNumber: string;
  address: string;
  onConfirm: (newEndDate: string) => Promise<void>;
}

const ExtendRentalModal: React.FC<ExtendRentalModalProps> = ({
  open,
  onClose,
  currentEndDate,
  apartmentNumber,
  address,
  onConfirm
}) => {
  const [newEndDate, setNewEndDate] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const minDate = dayjs(currentEndDate).add(1, 'day');

  const handleConfirm = async () => {
    if (!newEndDate) {
      setError('Vänligen välj ett nytt slutdatum');
      return;
    }

    if (newEndDate.isBefore(minDate)) {
      setError('Nytt slutdatum måste vara efter nuvarande slutdatum');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Ett datum, inte en tidpunkt. toISOString() gav "2027-11-29T23:00:00Z"
      // för 30 november — och det var den strängen styrelsen fick i mejlet.
      await onConfirm(newEndDate.format('YYYY-MM-DD'));
      setSuccess(true);

      // Stäng modal efter 2 sekunder
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setNewEndDate(null);
      }, 2000);
    } catch (err) {
      console.error('Error extending rental:', err);
      setError('Ett fel uppstod när förlängningen skulle begäras. Försök igen.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setNewEndDate(null);
      setError(null);
      setSuccess(false);
    }
  };

  // Visa bekräftelse om lyckad
  if (success) {
    return (
      <Dialog open={open} className="fixed z-50 inset-0 flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-md mx-auto p-8 text-center">
          <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-100">
            <CheckCircle sx={{ fontSize: 48, color: '#22c55e' }} />
          </div>

          <h2 className="text-xl font-bold text-[#222222] font-['Poppins'] mb-2">
            Förlängning begärd!
          </h2>

          <p className="text-sm text-[#626262]">
            Din förening har fått en förfrågan om att förlänga uthyrningen. Du kommer att få ett meddelande när beslut är taget.
          </p>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="fixed z-50 inset-0 flex items-center justify-center p-4"
      PaperProps={{
        style: { backgroundColor: 'transparent', boxShadow: 'none', overflow: 'visible' }
      }}
    >
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-4xl mx-auto max-h-[90vh] flex flex-col">
        <button
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-zinc-100 text-zinc-500 z-10"
          onClick={handleClose}
          disabled={loading}
          aria-label="Stäng"
        >
          <Close className="w-5 h-5" />
        </button>

        <div className="overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <CalendarMonth sx={{ fontSize: 24, color: '#3B82F6' }} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#222222] font-['Poppins']">
                Förläng uthyrning
              </h2>
              <p className="text-sm text-[#626262]">
                {address} {apartmentNumber && `Lgh ${apartmentNumber}`}
              </p>
            </div>
          </div>

          {/* Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900 font-semibold mb-2">
              ℹ️ Hur fungerar förlängning?
            </p>
            <ul className="text-sm text-blue-800 space-y-1 ml-4 list-disc">
              <li>Du väljer ett nytt slutdatum för uthyrningen</li>
              <li>Din förening granskar och godkänner förlängningen</li>
              <li>Din uthyrning fortsätter vara aktiv under väntan</li>
            </ul>
          </div>

          {/* Nuvarande slutdatum */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-xs font-semibold text-[#626262] mb-1">Nuvarande slutdatum:</p>
            <p className="text-lg font-bold text-[#222222]">
              {dayjs(currentEndDate).locale('sv').format('D MMMM YYYY')}
            </p>
          </div>

          {/* Nytt slutdatum */}
          <div>
            <label className="block text-sm font-semibold text-[#222222] mb-2">
              Nytt slutdatum *
            </label>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sv">
              <DatePicker
                value={newEndDate}
                onChange={(date) => setNewEndDate(date)}
                minDate={minDate}
                label="Välj datum"
                format="YYYY-MM-DD"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!error && !newEndDate,
                    helperText: !newEndDate ? 'Välj ett datum efter nuvarande slutdatum' : ''
                  }
                }}
              />
            </LocalizationProvider>
          </div>

          {/* Action buttons */}
          <div className="border-t border-[#E2EAF0] pt-4 mt-4">
            <div className="flex gap-2">
              <button
                onClick={handleConfirm}
                disabled={loading || !newEndDate}
                className="flex-1 bg-blue-500 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {loading ? '⏳ Skickar begäran...' : '✓ Begär förlängning'}
              </button>
              <button
                onClick={handleClose}
                disabled={loading}
                className="flex-1 border border-gray-300 text-gray-700 font-bold py-2.5 px-4 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ExtendRentalModal;
